import currencyFormatter from '~/utils/currencyFormatter';

const UNAVAILABLE = 'N/A';

export interface HotelSearchOrderingScore {
  kind: 'hotel';
  finalScore: number;
  globalFinalScore?: number;
  viewsCount?: number;
  salesCount?: number;
  salesAmount?: number;
  marginAmount?: number;
  marginPerView?: number;
  ctr?: number;
  overrideScore?: number;
  overrideExpireDate?: string;
  info?: string;
}

export interface LegacySearchOrderingScore extends Partial<App.OfferScore> {
  kind: 'legacy';
}

export type SearchOrderingScore = HotelSearchOrderingScore | LegacySearchOrderingScore;

export interface SearchOrderingMetric {
  label: string;
  value: string;
}

export interface LegacySearchOrderingRow {
  attribute: string;
  value: string;
  valueInfo?: string | false;
  score: string;
  scoreInfo?: string | false;
  weight: string;
}

export type SearchOrderingView =
  | {
      kind: 'hotel';
      headline: string;
      info?: string;
      rows: Array<SearchOrderingMetric>;
    }
  | {
      kind: 'legacy';
      headline: string;
      rows: Array<LegacySearchOrderingRow>;
    };

function formatDecimal(value: number | undefined): string {
  return typeof value === 'number' ? value.toFixed(3) : UNAVAILABLE;
}

function formatCount(value: number | undefined): string {
  return typeof value === 'number' ? value.toLocaleString() : UNAVAILABLE;
}

function formatMoney(value: number | undefined): string {
  return typeof value === 'number' ? currencyFormatter('AUD', value) : UNAVAILABLE;
}

function formatCtr(value: number | undefined): string {
  return typeof value === 'number' ? `${(value * 100).toFixed(2)}%` : UNAVAILABLE;
}

function formatWeight(value: number | undefined): string {
  return typeof value === 'number' ? `${value * 100}%` : UNAVAILABLE;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}

function readNumber(record: Record<string, unknown>, key: string): number | undefined {
  const value = record[key];
  return typeof value === 'number' ? value : undefined;
}

function readString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}

// Hotel admin scores omit calculatedScore. Search ranks the offer on sales_mpv.
export function normalizeSearchOrderingScore(score: unknown): SearchOrderingScore {
  if (!isRecord(score)) {
    return { kind: 'legacy' };
  }

  const finalScore = readNumber(score, 'sales_mpv');
  if (typeof finalScore === 'number') {
    return {
      kind: 'hotel',
      finalScore,
      globalFinalScore: readNumber(score, 'global_sales_mpv'),
      viewsCount: readNumber(score, 'viewsCount'),
      salesCount: readNumber(score, 'salesCount'),
      salesAmount: readNumber(score, 'salesAmount'),
      marginAmount: readNumber(score, 'marginAmount'),
      marginPerView: readNumber(score, 'mpv'),
      ctr: readNumber(score, 'ctr'),
      overrideScore: readNumber(score, 'overrideScore'),
      overrideExpireDate: readString(score, 'overrideExpireDate'),
      info: readString(score, 'info'),
    };
  }

  return {
    kind: 'legacy',
    ...(score as Partial<App.OfferScore>),
  };
}

function legacyExpectedValueWeight(score: LegacySearchOrderingScore): number | undefined {
  if (typeof score.expectedValueWeight !== 'number') {
    return undefined;
  }

  if (score.tierWeight) {
    return score.expectedValueWeight * (1 - score.tierWeight);
  }

  return score.expectedValueWeight;
}

function hotelRows(score: HotelSearchOrderingScore): Array<SearchOrderingMetric> {
  const rows: Array<SearchOrderingMetric> = [
    { label: 'Views', value: formatCount(score.viewsCount) },
    { label: 'Sales', value: formatCount(score.salesCount) },
    { label: 'Sales amount', value: formatMoney(score.salesAmount) },
    { label: 'Margin', value: formatMoney(score.marginAmount) },
    { label: 'Margin per view', value: formatDecimal(score.marginPerView) },
    { label: 'CTR', value: formatCtr(score.ctr) },
    { label: 'Manual override', value: formatDecimal(score.overrideScore) },
  ];

  if (score.overrideScore) {
    rows.push({ label: 'Override expires', value: score.overrideExpireDate || UNAVAILABLE });
  }

  rows.push({ label: 'Global sales MPV', value: formatDecimal(score.globalFinalScore) });

  return rows;
}

export function getSearchOrderingView(score: SearchOrderingScore): SearchOrderingView {
  if (score.kind === 'hotel') {
    return {
      kind: 'hotel',
      headline: `Calculated search score: ${formatDecimal(score.finalScore)}`,
      info: score.info,
      rows: hotelRows(score),
    };
  }

  return {
    kind: 'legacy',
    headline: `Calculated search score: ${formatDecimal(score.calculatedScore)}`,
    rows: [
      {
        attribute: 'Expected Value',
        value: formatDecimal(score.expectedValue),
        valueInfo: !!score.expectedValue && score.expectedValueInfo,
        score: formatDecimal(score.adjustedExpectedValue),
        scoreInfo: !!score.adjustedExpectedValue && score.adjustedExpectedValueInfo,
        weight: formatWeight(legacyExpectedValueWeight(score)),
      },
      {
        attribute: 'Manual Override',
        value: UNAVAILABLE,
        score: formatDecimal(score.overrideScore),
        weight: formatWeight(score.overrideWeight),
      },
    ],
  };
}
